import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef } from "react";

import type { MemberDeviceUsage } from "@storyboard/web-client";
import { getPersonalDeviceMembers, memberKeys } from "@storyboard/web-client";
import { transport } from "@/config";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { recordEvent } from "@/services/telemetry";

/**
 * Slow on purpose: a driver reconfiguring a phone between work and personal mode is a rare event, unlike the
 * per-minute heartbeat that drives the rest of this column.
 */
const DEVICE_USAGE_REFRESH_MS = 10 * 60_000;

/**
 * Membership ids of members registered on at least one personal-mode device.
 *
 * The endpoint returns every device linked to each member, so the "has a personal device" predicate lives
 * here rather than server-side — a driver carrying both a personal phone and a work handset counts.
 */
export function personalDeviceMembershipIds(members: readonly MemberDeviceUsage[]): Set<string> {
  return new Set(
    members
      .filter((member) => member.devices.some((device) => device.deviceMode === "personal"))
      .map((member) => member.workspaceMembershipId),
  );
}

/**
 * Membership ids of members registered only on personal-mode devices: at least one, and no work device.
 *
 * Such a driver has no hands-free playback anywhere, unlike one who carries a work handset beside a personal phone.
 */
export function personalOnlyMembershipIds(members: readonly MemberDeviceUsage[]): Set<string> {
  return new Set(
    members
      .filter(
        (member) =>
          member.devices.some((device) => device.deviceMode === "personal") &&
          !member.devices.some((device) => device.deviceMode === "work"),
      )
      .map((member) => member.workspaceMembershipId),
  );
}

/**
 * Members of the current workspace currently registered on at least one personal-mode device, and those registered on
 * personal-mode devices alone.
 *
 * Polled rather than pushed. The rest of the roster rides store queries fed by AppSync, but device
 * registrations are only published to the owning account's channel, never the dispatcher's, so a mode switch
 * surfaces on the next refresh instead of immediately. A failed refresh keeps the last known set (Query holds
 * the previous data on error); only a failed first load leaves the roster without icons. The `select` sorts by
 * membership id so a reordered response still structurally shares with the previous one instead of producing
 * a fresh set with identical contents.
 */
export function usePersonalDeviceMembers() {
  const { currentWorkspaceId } = useContext(WorkspaceContext);

  const query = useQuery({
    queryKey: memberKeys.deviceUsage(currentWorkspaceId ?? ""),
    queryFn: () => getPersonalDeviceMembers(transport, { workspaceId: currentWorkspaceId ?? "" }),
    enabled: Boolean(currentWorkspaceId),
    refetchInterval: DEVICE_USAGE_REFRESH_MS,
    // Match the old setInterval cadence: keep ticking in background tabs, and don't let window
    // focus refire this deliberately slow roster-wide endpoint.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    select: (data) =>
      [...(data.members ?? [])].sort((a, b) => a.workspaceMembershipId.localeCompare(b.workspaceMembershipId)),
  });

  // Failures are swallowed by design, so Honeycomb is the only place a persistently failing endpoint
  // shows up: stale indicators look identical to correct ones. The ref seeds with the cached value so
  // a remount within gcTime doesn't re-record an already-reported failure.
  const lastRecordedErrorAt = useRef(query.errorUpdatedAt);
  useEffect(() => {
    if (query.errorUpdatedAt && query.errorUpdatedAt !== lastRecordedErrorAt.current && query.error) {
      lastRecordedErrorAt.current = query.errorUpdatedAt;
      recordEvent("device_usage.refresh_failed", {
        deviceUsage: {
          workspaceId: currentWorkspaceId,
          error: query.error instanceof Error ? query.error.message : String(query.error),
        },
      });
    }
  }, [query.errorUpdatedAt]);

  const memberships = useMemo(() => personalDeviceMembershipIds(query.data ?? []), [query.data]);
  const personalOnly = useMemo(() => personalOnlyMembershipIds(query.data ?? []), [query.data]);

  return { personalDeviceMembershipIds: memberships, personalOnlyMembershipIds: personalOnly };
}
